import axios from 'axios';
import { AuthService } from '@/services/auth.ts'
import { useToast } from '@/components/ui/toast'
import { h } from 'vue'
import { CircleXIcon } from 'lucide-vue-next'

const { toast } = useToast()

function showToast(description: string) {
  toast({
    description: h('div', { class: 'flex items-center' }, [
      h(CircleXIcon, { class: 'size-6 text-white fill-red-500 mr-4' }),
      h('span', description),
    ]),
    type: 'foreground',
  })
}

// Create an axios instance
const httpClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL
});

// Add a request interceptor to add the access token to every request
httpClient.interceptors.request.use((config) => {
  const token = AuthService.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config
});

// Refresh token before request if access token is expired

// httpClient.interceptors.response.use(
//   response => response,
//   error => {
//     if (error.response?.status === 401) {
//       const authStore = useAuthStore()
//       authStore.clearToken()
//       // Redirecionar para login
//     }
//     return Promise.reject(error)
//   }
// )

const cachedData = { isRefreshing: false }

// Refresh token before request if access token is expired
async function refreshTokenMiddleware() {
  if (!cachedData.isRefreshing && AuthService.shouldRefreshToken()) {
    cachedData.isRefreshing = true;
    try {
      const response = await httpClient.post('/auth/refresh/', {
        refresh: AuthService.getRefreshToken(),
      })
      AuthService.setTokens(response.data.access, response.data.refresh, true)
    } catch {
      AuthService.clearTokens()
    }
    cachedData.isRefreshing = false;
  } else if (cachedData.isRefreshing) {
    // Wait until the token is refreshed
    await new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (!cachedData.isRefreshing) {
          clearInterval(interval);
          resolve();
        }
      }, 100);

      // Add timeout to prevent waiting indefinitely
      setTimeout(() => {
        clearInterval(interval);
        resolve();
      }, 10000); // 10 second timeout
    });
  }
}

export class HttpService {
  static async get<T>(url: string, headers: Record<string, string> = {}): Promise<T> {
    await refreshTokenMiddleware()
    try {
      const response = await httpClient.get<T>(url, { headers })
      return response.data
    } catch (error) {
      // Show the full error response content in the toast
      const errorMessage = error.response?.data?.message || error.response?.data?.description || error.response?.statusText || error.response?.data || 'An unknown error occurred'
      if ( !(error.response.status === 404 && url.endsWith("/hubspot/integration/")) ) {
        showToast(errorMessage)
      }
      throw error
    }
  }

  static async getBlob(url: string, headers: Record<string, string> = {}): Promise<{ blob: Blob; filename?: string }> {
    await refreshTokenMiddleware()
    const response = await httpClient.get(url, {
      responseType: 'blob',
      headers
    })

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
    let filename: string | undefined

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    return { blob: response.data, filename }
  }

  static async post<T>(url: string, data: unknown, headers: Record<string, string> = {}): Promise<T> {
    await refreshTokenMiddleware()
    try {
      const response = await httpClient.post<T>(url, data, { headers })
      return response.data
    } catch (error) {
      // Show the full error response content in the toast
      const errorMessage = error.response?.data?.message || error.response?.data?.description || error.response.data.detail || error.response?.statusText || error.response?.data || 'An unknown error occurred'
      showToast(errorMessage)
      throw error
    }
  }

  /**
   * POST without the error toast. For fire-and-forget calls (activity tracking)
   * whose failure must never be surfaced to the user; the promise still rejects
   * so callers can decide what to do.
   */
  static async postQuiet<T>(url: string, data: unknown, headers: Record<string, string> = {}): Promise<T> {
    await refreshTokenMiddleware()
    const response = await httpClient.post<T>(url, data, { headers })
    return response.data
  }

  static async postBlob(url: string, data: unknown, headers: Record<string, string> = {}): Promise<{ blob: Blob; filename?: string }> {
    await refreshTokenMiddleware()
    const response = await httpClient.post(url, data, {
      responseType: 'blob',
      headers
    })

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
    let filename: string | undefined

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    return { blob: response.data, filename }
  }

  static async put<T>(url: string, data: unknown, headers: Record<string, string> = {}): Promise<T> {
    await refreshTokenMiddleware()
    try {
      const response = await httpClient.put<T>(url, data, { headers })
      return response.data
    } catch (error) {
      // Show the full error response content in the toast
      const errorMessage = error.response?.data?.message || error.response?.data?.description || error.response?.statusText || error.response?.data || 'An unknown error occurred'
      showToast(errorMessage)
      throw error
    }
  }

  static async delete<T>(url: string, headers: Record<string, string> = {}): Promise<T> {
    await refreshTokenMiddleware()
    try {
      const response = await httpClient.delete<T>(url, { headers })
      return response.data
    } catch (error) {
      // Show the full error response content in the toast
      const errorMessage = error.response?.data?.message || error.response?.data?.description || error.response?.statusText || error.response?.data || 'An unknown error occurred'
      showToast(errorMessage)
      throw error
    }
  }
}
